import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { ExpandableText, OverflowList } from '../../ui/expandable-text';

export type SummaryValue = string | string[] | ReactNode | null | undefined;

export interface SummaryRowData {
  label: string;
  value: SummaryValue;
  /** Texto longo de uma linha que expande (AC-66). */
  expandable?: boolean;
  testId?: string;
}

function isEmpty(value: SummaryValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export interface SummarySectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: string;
  rows: SummaryRowData[];
  onEdit?: () => void;
  editLabel?: string;
  /** Renderiza mesmo sem linhas (ex.: seção sempre presente). */
  alwaysRender?: boolean;
  'data-testid'?: string;
}

/**
 * Seção de resumo: título + "Editar" + linhas rótulo/valor. Linha sem valor e
 * seção sem linha não aparecem (salvo `alwaysRender`). Multivalor vira
 * `OverflowList` ("+N"); texto marcado `expandable` vira `ExpandableText`.
 */
function SummarySection({
  title,
  rows,
  onEdit,
  editLabel = 'Editar',
  alwaysRender = false,
  className,
  'data-testid': testId,
  ...props
}: SummarySectionProps) {
  const headingId = useId();
  const visible = rows.filter((row) => !isEmpty(row.value));
  if (visible.length === 0 && !alwaysRender) return null;

  return (
    <section
      aria-labelledby={headingId}
      data-slot="summary-section"
      data-testid={testId}
      className={cn('flex flex-col gap-2', className)}
      {...props}
    >
      <div className="flex items-center justify-between gap-4">
        <h2 id={headingId} className="text-lg font-medium leading-7 text-foreground">
          {title}
        </h2>
        {onEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onEdit}
            aria-label={`${editLabel} ${title}`}
            data-testid={testId ? `${testId}-edit` : undefined}
            className="min-h-11 text-xs"
          >
            {editLabel}
          </Button>
        ) : null}
      </div>
      <dl className="flex flex-col gap-2">
        {visible.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-6">
            <dt className="shrink-0 text-base leading-6 text-foreground">{row.label}</dt>
            <dd className="min-w-0 flex-1 text-right text-base font-medium leading-6 text-foreground">
              {Array.isArray(row.value) ? (
                <OverflowList items={row.value as string[]} data-testid={row.testId} />
              ) : typeof row.value === 'string' && row.expandable ? (
                <ExpandableText data-testid={row.testId}>{row.value}</ExpandableText>
              ) : (
                <span data-testid={row.testId}>{row.value}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
SummarySection.displayName = 'SummarySection';

function SummaryList({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div data-slot="summary-list" className={cn('flex flex-col gap-8', className)} {...props} />
  );
}
SummaryList.displayName = 'SummaryList';

export { SummaryList, SummarySection };
